import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import AdmissionsActionsMenu from 'components/admissions/AdmissionsActionsMenu';
import { StatusChip } from 'components/referrals/referralsTableColumns';
import CircularProgress from 'components/shared/CircularProgress';
import { simpleDateAtTime } from 'lib/date';
import { capitalize } from 'lib/util';
import Admission from 'models/reviews/Admission';
import colors from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

const columnHelper = createColumnHelper<Admission>();

type Props = {
  onClickAdmission: (id: string) => void;
};

export default function admissionsTableColumns({ onClickAdmission }: Props) {
  return [
    columnHelper.accessor((row) => row.data.patient?.name, {
      id: 'patient',
      header: 'Patient Name',
      cell: (info) => {
        const value = info.getValue();
        return (
          <PatientNameContainer>
            {info.row.original.isProcessing && (
              <div>
                <CircularProgress size={16} thickness={2} color={colors.primaryBlue} />
              </div>
            )}
            <PatientNameLink fontWeight='700' onClick={() => onClickAdmission(info.row.original.id)}>
              {value || <>&mdash;</>}
            </PatientNameLink>
          </PatientNameContainer>
        );
      },
      size: 100,
    }),
    columnHelper.accessor((row) => row.data.referralSource, {
      id: 'referralSource',
      header: 'Referral Source',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <>&mdash;</>;
        return <div>{value}</div>;
      },
      size: 100,
    }),
    columnHelper.accessor((row) => row.group, {
      id: 'group',
      header: 'SNF',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <>&mdash;</>;
        return <div>{value.name}</div>;
      },
      size: 100,
    }),
    columnHelper.accessor((row) => row.updatedAt, {
      id: 'updatedAt',
      header: 'Last Updated',
      cell: (info) => (info.getValue() ? simpleDateAtTime(info.getValue() ?? '') : <>&mdash;</>),
      size: 100,
    }),
    columnHelper.accessor((row) => row.createdBy?.fullName, {
      id: 'createdBy',
      header: 'Created By',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <>&mdash;</>;
        return <div>{value}</div>;
      },
      size: 100,
    }),
    columnHelper.accessor((row) => row.status, {
      id: 'status',
      header: 'Status',
      cell: (info) => (
        <StatusChip $status={info.row.original.status}>
          {info.row.original.rejected ? 'Denied' : capitalize(info.row.original.status)}
        </StatusChip>
      ),
      size: 100,
    }),
    columnHelper.display({
      id: 'menu',
      cell: (info) => <AdmissionsActionsMenu admission={info.row.original} />,
      size: 40,
      meta: { style: { justifyContent: 'flex-end' } },
    }),
  ];
}

const PatientNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PatientNameLink = styled(BodySmall)`
  cursor: pointer;
  color: ${colors.primaryBlue};
`;
